import { CompactInlineAlertWidget, InlineAlertType } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { MapDispatchToProps, MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { Set } from '~commonInfra/utils/Set';
import { CompositeSessionInfoSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { AppState } from '~src/bootstrap/redux/state/AppState';
import { Formatters, Messages } from '~src/codegen/Localize';
import { CollectionMapper } from '~src/Collections/mappers/CollectionMapper';
import { L10n } from '~src/commonInfra/L10n/L10n';
import { incrementRouterSpaceReactKey } from '~src/spacesPlatform/actions/IncrementRouterSpaceReactKeyAction';
import {
  CreatedAtDetailSection,
  ModifiedAtDetailSection
} from '~src/spacesPlatform/components/content/contentDetailDialog/CommonContentDetailSections';
import { ContentDetailSection, sectionContainer } from '~src/spacesPlatform/components/content/contentDetailDialog/ContentDetailSection';
import { ConnectedDeleteContentSection } from '~src/spacesPlatform/components/content/contentDetailDialog/DeleteContentSection';
import {
  COLLECTION_DESCRIPTION_MAX_LENGTH,
  DescriptionDetailSection
} from '~src/spacesPlatform/components/content/contentDetailDialog/DescriptionDetailSection';
import { ConnectedOwnerDetailSection } from '~src/spacesPlatform/components/content/contentDetailDialog/OwnerDetailSection';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';

import { CollectionsResourceQuery } from '../ResourceQuery/CollectionsResourceQuery';
import { ICollectionInfo } from './ICollectionInfo';


export interface CollectionDetailsDialogBodyOwnProps {
  readonly collection: ICollectionInfo;
}

export interface CollectionDetailsDialogBodyStateProps {
  readonly urls: SpacesSiteUrls;
  readonly isAdminOrOwner: boolean;
}

interface CollectionDetailsDialogBodyDispatchProps {
  readonly incrementRouterSpaceReactKey: () => void;
}

export type CollectionDetailsDialogBodyProps =
    CollectionDetailsDialogBodyOwnProps &
    CollectionDetailsDialogBodyStateProps &
    CollectionDetailsDialogBodyDispatchProps;

export function CollectionDetailsDialogBody(props: CollectionDetailsDialogBodyProps): JSX.Element {

  const setDescription = React.useCallback(async (description: string) => {
    const updatedCollectionData = { luid: props.collection.luid, description };
    const response = await new CollectionsResourceQuery().update([updatedCollectionData]);

    if (response.errors && response.errors.length > 0) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: Messages.collectionSetDescriptionError()
      });
    }

    return CollectionMapper.mapUpdateCollectionsResponseDataToIResult(response, Set.singletonForIdItem(props.collection));
  }, [props.collection]);

  return (
    <div className={sectionContainer}>
      <DescriptionDetailSection
        description={props.collection.description}
        isActionEnabled={props.isAdminOrOwner}
        setDescription={setDescription}
        descriptionMaxLength={COLLECTION_DESCRIPTION_MAX_LENGTH}
        incrementRouterSpaceReactKey={props.incrementRouterSpaceReactKey}
      />
      <ConnectedOwnerDetailSection content={props.collection} isActionEnabled={props.isAdminOrOwner} urls={props.urls}/>
      <CollectionItemCountSection collection={props.collection} />
      <CreatedAtDetailSection date={props.collection.createdAt} />
      <ModifiedAtDetailSection date={props.collection.updatedAt} />
      {props.isAdminOrOwner &&
        <ConnectedDeleteContentSection
          actionMessage={Messages.collectionsDetailsDelete()}
          content={props.collection}
        />
      }
    </div>
  );
}

export interface CollectionItemCountSectionProps {
  readonly collection: ICollectionInfo;
}

export function CollectionItemCountSection(props: CollectionItemCountSectionProps): JSX.Element {
  const hiddenItemCount = props.collection.totalItemCount - props.collection.permissionedItemCount;
  const detail: JSX.Element =
    <>
      {L10n.localizedCount(props.collection.totalItemCount)}
      { hiddenItemCount > 0 &&
        <CompactInlineAlertWidget
          testId={'collection-detail-count'}
          type={InlineAlertType.Info}
          style={{ marginTop: '8px'}}
        >
          {Messages.collectionsDetailsItemCountPermissionedWarning({ITEM_COUNT: hiddenItemCount, ITEM_COUNT_LOC: Formatters.formatCount(hiddenItemCount)})}
        </CompactInlineAlertWidget>
      }
    </>;

  return (
    <ContentDetailSection
      header={Messages.collectionsDetailsItemCount()}
      testId={'item-count'}
      detail={detail}
    />
  );
}

const mapDispatchToProps: MapDispatchToProps<CollectionDetailsDialogBodyDispatchProps> = (dispatch) => bindActionCreators({
  incrementRouterSpaceReactKey: incrementRouterSpaceReactKey
}, dispatch);

const mapStateToProps: MapStateToProps<CollectionDetailsDialogBodyStateProps> = (state: AppState, ownProps: CollectionDetailsDialogBodyOwnProps) => ({
  isAdminOrOwner: CompositeSessionInfoSelectors.currentUserIsAdminOrOwnerSelector(ownProps.collection.owner)(state),
  urls: siteUrlsSelector(state),
});

export const ConnectedCollectionDetailsDialogBody: React.ComponentClass<CollectionDetailsDialogBodyOwnProps> =
  connect(mapStateToProps, mapDispatchToProps)(CollectionDetailsDialogBody);
